import type { ReactNode } from 'react';
import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import {
  createTheme as createMuiTheme,
  ThemeProvider as MuiThemeProvider,
  alpha,
} from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { palette, darkPalette } from '../theme/palette';

declare module '@mui/material/styles' {
  interface Palette {
    venue: { gaming: string; snooker: string; kids: string };
    surface: { glass: string; glassLight: string; glassBorder: string; hover: string };
    sidebar: {
      bg: string;
      text: string;
      textActive: string;
      activeIndicator: string;
      activeBg: string;
      hover: string;
      border: string;
      sectionLabel: string;
    };
    gradients: {
      primary: string;
      secondary: string;
      hero: string;
      heroHeadline: string;
      buttonPrimary: string;
      buttonPrimaryHover: string;
      card: string;
      glow: string;
      text: string;
    };
  }
  interface PaletteOptions {
    venue?: { gaming: string; snooker: string; kids: string };
    surface?: { glass: string; glassLight: string; glassBorder: string; hover: string };
    sidebar?: {
      bg: string;
      text: string;
      textActive: string;
      activeIndicator: string;
      activeBg: string;
      hover: string;
      border: string;
      sectionLabel: string;
    };
    gradients?: {
      primary: string;
      secondary: string;
      hero: string;
      heroHeadline: string;
      buttonPrimary: string;
      buttonPrimaryHover: string;
      card: string;
      glow: string;
      text: string;
    };
  }
}

type ThemeMode = 'light' | 'dark';

interface ThemeContextValue {
  mode: ThemeMode;
  toggleTheme: () => void;
  setTheme: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const STORAGE_KEY = 'lov_theme_mode';

function getInitialTheme(): ThemeMode {
  if (typeof window === 'undefined') return 'light';
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    // localStorage unavailable
  }
  try {
    if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) return 'dark';
  } catch {
    // matchMedia unavailable
  }
  return 'light';
}

function buildTheme(p: typeof palette | typeof darkPalette) {
  return createMuiTheme({
    palette: {
      mode: p.mode,
      primary: p.primary,
      secondary: p.secondary,
      background: p.background,
      text: p.text,
      divider: p.divider,
      error: p.error,
      warning: p.warning,
      success: p.success,
      info: p.info,
      surface: p.surface,
      sidebar: p.sidebar,
      gradients: p.gradients,
      venue: p.venue,
    },

    typography: {
      fontFamily: '"Poppins", system-ui, -apple-system, sans-serif',
      h1: {
        fontFamily: '"Poppins", sans-serif',
        fontWeight: 700,
        letterSpacing: '-0.02em',
        color: p.text.primary,
      },
      h2: {
        fontFamily: '"Poppins", sans-serif',
        fontWeight: 700,
        letterSpacing: '-0.015em',
        color: p.text.primary,
      },
      h3: {
        fontFamily: '"Poppins", sans-serif',
        fontWeight: 600,
        letterSpacing: '-0.01em',
        color: p.text.primary,
      },
      h4: { fontFamily: '"Poppins", sans-serif', fontWeight: 600, color: p.text.primary },
      h5: { fontFamily: '"Poppins", sans-serif', fontWeight: 600, color: p.text.primary },
      h6: { fontFamily: '"Poppins", sans-serif', fontWeight: 600, color: p.text.primary },
      subtitle1: { fontWeight: 500, letterSpacing: '-0.005em', color: p.text.secondary },
      subtitle2: { fontWeight: 500, letterSpacing: '-0.005em', color: p.text.secondary },
      body1: { letterSpacing: '-0.003em', lineHeight: 1.65, color: p.text.primary },
      body2: { letterSpacing: '-0.003em', lineHeight: 1.6, color: p.text.secondary },
      button: {
        textTransform: 'none',
        fontWeight: 600,
        fontFamily: '"Poppins", sans-serif',
        letterSpacing: '0.01em',
      },
      caption: { letterSpacing: '0.01em', color: p.text.secondary },
      overline: { letterSpacing: '0.08em', fontWeight: 600, textTransform: 'uppercase' },
    },

    shape: { borderRadius: 12 },

    shadows: [
      'none',
      '0 1px 2px rgba(0,0,0,0.05)',
      '0 1px 3px rgba(0,0,0,0.08)',
      '0 4px 6px rgba(0,0,0,0.07)',
      '0 4px 8px rgba(0,0,0,0.08)',
      '0 6px 12px rgba(0,0,0,0.08)',
      '0 8px 16px rgba(0,0,0,0.08)',
      '0 10px 20px rgba(0,0,0,0.08)',
      '0 12px 24px rgba(0,0,0,0.1)',
      '0 14px 28px rgba(0,0,0,0.1)',
      '0 16px 32px rgba(0,0,0,0.1)',
      '0 18px 36px rgba(0,0,0,0.1)',
      '0 20px 40px rgba(0,0,0,0.12)',
      '0 22px 44px rgba(0,0,0,0.12)',
      '0 24px 48px rgba(0,0,0,0.12)',
      '0 26px 52px rgba(0,0,0,0.12)',
      '0 28px 56px rgba(0,0,0,0.14)',
      '0 30px 60px rgba(0,0,0,0.14)',
      '0 32px 64px rgba(0,0,0,0.14)',
      '0 34px 68px rgba(0,0,0,0.14)',
      '0 36px 72px rgba(0,0,0,0.16)',
      '0 38px 76px rgba(0,0,0,0.16)',
      '0 40px 80px rgba(0,0,0,0.16)',
      '0 42px 84px rgba(0,0,0,0.16)',
      '0 44px 88px rgba(0,0,0,0.18)',
    ],

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: { scrollBehavior: 'smooth' },
          body: {
            backgroundColor: p.background.default,
            minHeight: '100vh',
            fontFamily: '"Poppins", system-ui, sans-serif',
          },
          '::-webkit-scrollbar': { width: '8px', height: '8px' },
          '::-webkit-scrollbar-track': { background: p.background.default },
          '::-webkit-scrollbar-thumb': {
            background: alpha(p.text.secondary, 0.2),
            borderRadius: '4px',
            '&:hover': { background: alpha(p.text.secondary, 0.3) },
          },
          '::selection': {
            backgroundColor: alpha(p.primary.main, 0.2),
            color: p.text.primary,
          },
          '*:focus-visible': {
            outline: `3px solid ${p.primary.main}`,
            outlineOffset: '2px',
            borderRadius: '4px',
          },
          'a:focus-visible, button:focus-visible, [role="button"]:focus-visible': {
            outline: `3px solid ${p.primary.main}`,
            outlineOffset: '2px',
          },
        },
      },

      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 10,
            padding: '12px 24px',
            fontSize: '0.9375rem',
            fontFamily: '"Poppins", sans-serif',
            fontWeight: 600,
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:active': { transform: 'scale(0.98)' },
          },
          contained: {
            backgroundColor: p.primary.main,
            color: p.primary.contrastText,
            boxShadow: `0 4px 12px ${alpha(p.primary.main, 0.3)}`,
            '&:hover': {
              backgroundColor: p.primary.dark,
              boxShadow: `0 6px 16px ${alpha(p.primary.main, 0.4)}`,
              transform: 'translateY(-1px)',
            },
          },
          containedSecondary: {
            backgroundColor: p.secondary.main,
            color: p.secondary.contrastText,
            boxShadow: `0 4px 12px ${alpha(p.secondary.main, 0.3)}`,
            '&:hover': {
              backgroundColor: p.secondary.dark,
              boxShadow: `0 6px 16px ${alpha(p.secondary.main, 0.4)}`,
            },
          },
          outlined: {
            borderColor: p.surface.glassBorder,
            borderWidth: '1.5px',
            backgroundColor: 'transparent',
            color: p.text.primary,
            '&:hover': {
              borderColor: alpha(p.primary.main, 0.4),
              backgroundColor: p.surface.hover,
            },
          },
          text: {
            color: p.text.primary,
            '&:hover': { backgroundColor: p.surface.hover },
          },
          sizeLarge: { padding: '14px 32px', fontSize: '1rem' },
          sizeSmall: { padding: '8px 16px', fontSize: '0.8125rem' },
        },
      },

      MuiTextField: {
        defaultProps: { variant: 'outlined', fullWidth: true, InputLabelProps: { shrink: true } },
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              backgroundColor: p.mode === 'dark' ? p.background.paper : p.background.paper,
              borderRadius: 10,
              fontFamily: '"Poppins", sans-serif',
              '& fieldset': { borderColor: p.surface.glassBorder, borderWidth: '1.5px' },
              '&:hover fieldset': { borderColor: alpha(p.primary.main, 0.3) },
              '&.Mui-focused fieldset': {
                borderColor: p.primary.main,
                borderWidth: '2px',
                boxShadow: `0 0 0 3px ${alpha(p.primary.main, 0.1)}`,
              },
            },
            '& .MuiInputLabel-root': {
              color: p.text.secondary,
              fontFamily: '"Poppins", sans-serif',
              '&.Mui-focused': { color: p.primary.main },
            },
            '& .MuiInputBase-input': {
              padding: '14px 16px',
              color: p.text.primary,
              fontFamily: '"Poppins", sans-serif',
            },
          },
        },
      },

      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: p.background.paper,
            border: `1px solid ${p.surface.glassBorder}`,
            borderRadius: 16,
          },
          elevation1: { boxShadow: '0 4px 12px rgba(0,0,0,0.06)' },
          elevation2: { boxShadow: '0 6px 16px rgba(0,0,0,0.08)' },
          elevation3: { boxShadow: '0 8px 24px rgba(0,0,0,0.1)' },
        },
      },

      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: p.background.paper,
            border: `1px solid ${p.surface.glassBorder}`,
            borderRadius: 16,
            boxShadow:
              p.mode === 'dark' ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.05)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 8px 24px ${alpha(p.primary.main, 0.12)}`,
              borderColor: alpha(p.primary.main, 0.15),
            },
          },
        },
      },

      MuiCardContent: {
        styleOverrides: {
          root: { padding: 24, '&:last-child': { paddingBottom: 24 } },
        },
      },

      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor:
              p.mode === 'dark'
                ? alpha(p.background.default, 0.95)
                : alpha(p.background.default, 0.92),
            backdropFilter: 'blur(10px)',
            borderBottom: `1px solid ${p.surface.glassBorder}`,
            boxShadow: 'none',
            color: p.text.primary,
          },
        },
      },

      MuiToolbar: {
        styleOverrides: { root: { minHeight: '72px !important' } },
      },

      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: p.background.paper,
            borderLeft: `1px solid ${p.surface.glassBorder}`,
          },
        },
      },

      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            margin: '4px 8px',
            padding: '12px 16px',
            fontFamily: '"Poppins", sans-serif',
            transition: 'all 0.2s ease',
            '&:hover': { backgroundColor: p.surface.hover },
            '&.Mui-selected': {
              backgroundColor: alpha(p.primary.main, 0.1),
              '&:hover': { backgroundColor: alpha(p.primary.main, 0.14) },
            },
          },
        },
      },

      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            border: `1px solid ${p.surface.glassBorder}`,
            fontFamily: '"Poppins", sans-serif',
          },
          standardSuccess: {
            backgroundColor: alpha(p.success.main, 0.08),
            color: p.success.dark,
            '& .MuiAlert-icon': { color: p.success.main },
          },
          standardError: {
            backgroundColor: alpha(p.error.main, 0.08),
            color: p.error.dark,
            '& .MuiAlert-icon': { color: p.error.main },
          },
          standardWarning: {
            backgroundColor: alpha(p.warning.main, 0.08),
            color: p.warning.dark,
            '& .MuiAlert-icon': { color: p.warning.main },
          },
          standardInfo: {
            backgroundColor: alpha(p.info.main, 0.08),
            color: p.info.dark,
            '& .MuiAlert-icon': { color: p.info.main },
          },
        },
      },

      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 600,
            fontFamily: '"Poppins", sans-serif',
            fontSize: '0.75rem',
            transition: 'all 0.2s ease',
            '&:hover': { transform: 'scale(1.02)' },
          },
          filled: {
            backgroundColor: alpha(p.primary.main, 0.1),
            color: p.mode === 'dark' ? p.primary.light : p.primary.dark,
            '&:hover': { backgroundColor: alpha(p.primary.main, 0.16) },
          },
          outlined: { borderColor: p.surface.glassBorder, color: p.text.primary },
        },
      },

      MuiDivider: {
        styleOverrides: { root: { borderColor: p.surface.glassBorder } },
      },

      MuiIconButton: {
        styleOverrides: {
          root: {
            color: p.text.secondary,
            transition: 'all 0.2s ease',
            '&:hover': { backgroundColor: p.surface.hover, color: p.primary.main },
          },
        },
      },

      MuiLink: {
        styleOverrides: {
          root: {
            color: p.primary.main,
            textDecoration: 'none',
            fontWeight: 600,
            fontFamily: '"Poppins", sans-serif',
            transition: 'color 0.2s ease',
            '&:hover': { color: p.primary.dark, textDecoration: 'none' },
          },
        },
      },

      MuiCircularProgress: {
        styleOverrides: { root: { color: p.primary.main } },
      },

      MuiLinearProgress: {
        styleOverrides: {
          root: { backgroundColor: alpha(p.secondary.main, 0.15), borderRadius: 4 },
          bar: { backgroundColor: p.secondary.main, borderRadius: 4 },
        },
      },

      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: p.sidebar.bg,
            color: '#FFFFFF',
            borderRadius: 8,
            fontSize: '0.8125rem',
            padding: '8px 12px',
            fontFamily: '"Poppins", sans-serif',
            fontWeight: 500,
          },
          arrow: { color: p.sidebar.bg },
        },
      },

      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: p.background.paper,
            border: `1px solid ${p.surface.glassBorder}`,
            borderRadius: 12,
            marginTop: 8,
            boxShadow:
              p.mode === 'dark' ? '0 8px 24px rgba(0,0,0,0.4)' : '0 8px 24px rgba(0,0,0,0.12)',
          },
        },
      },

      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            margin: '4px 8px',
            padding: '10px 16px',
            fontFamily: '"Poppins", sans-serif',
            transition: 'all 0.2s ease',
            '&:hover': { backgroundColor: p.surface.hover },
            '&.Mui-selected': {
              backgroundColor: alpha(p.primary.main, 0.1),
              color: p.primary.main,
            },
          },
        },
      },

      MuiAvatar: {
        styleOverrides: {
          root: {
            backgroundColor: alpha(p.primary.main, 0.12),
            color: p.primary.main,
            border: `2px solid ${p.surface.glassBorder}`,
            fontFamily: '"Poppins", sans-serif',
            fontWeight: 700,
          },
        },
      },

      MuiBadge: {
        styleOverrides: {
          badge: { fontWeight: 700, fontFamily: '"Poppins", sans-serif' },
          colorPrimary: { backgroundColor: p.primary.main },
          colorSecondary: { backgroundColor: p.success.main },
        },
      },

      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            fontFamily: '"Poppins", sans-serif',
            fontSize: '0.9375rem',
            minHeight: 48,
            color: p.text.secondary,
            '&.Mui-selected': { color: p.primary.main, fontWeight: 600 },
          },
        },
      },

      MuiTabs: {
        styleOverrides: {
          indicator: { backgroundColor: p.primary.main, height: 3, borderRadius: 3 },
        },
      },

      MuiSkeleton: {
        styleOverrides: { root: { backgroundColor: alpha(p.text.secondary, 0.08) } },
      },

      MuiBackdrop: {
        styleOverrides: { root: { backgroundColor: alpha(p.sidebar.bg, 0.6) } },
      },

      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: p.background.paper,
            border: `1px solid ${p.surface.glassBorder}`,
            borderRadius: 20,
            boxShadow:
              p.mode === 'dark' ? '0 20px 60px rgba(0,0,0,0.5)' : '0 20px 60px rgba(0,0,0,0.15)',
          },
        },
      },

      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${p.surface.glassBorder}`,
            fontFamily: '"Poppins", sans-serif',
          },
          head: {
            fontFamily: '"Poppins", sans-serif',
            fontWeight: 600,
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            backgroundColor: alpha(p.background.default, 0.6),
            color: p.text.secondary,
          },
        },
      },

      MuiTableRow: {
        styleOverrides: {
          root: { '&:hover': { backgroundColor: p.surface.hover } },
        },
      },

      MuiInputAdornment: {
        styleOverrides: { root: { color: p.text.secondary } },
      },
    },
  });
}

export function ThemeContextProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<ThemeMode>(getInitialTheme);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      try {
        if (!localStorage.getItem(STORAGE_KEY)) setMode(e.matches ? 'dark' : 'light');
      } catch {
        // localStorage unavailable
      }
    };
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // localStorage unavailable
    }
    if (mode === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [mode]);

  const theme = useMemo(() => buildTheme(mode === 'dark' ? darkPalette : palette), [mode]);

  const contextValue: ThemeContextValue = {
    mode,
    toggleTheme: () => setMode((prev) => (prev === 'light' ? 'dark' : 'light')),
    setTheme: setMode,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) throw new Error('useTheme must be used within a ThemeContextProvider');
  return context;
}
